import _ from 'lodash';

import { BorrowerType } from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalArray, normalizeViewPhoneNumber } from 'utils/data';
import { getPersonFullName } from 'utils/loanApplication';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';
import { getSqlServerGuidSortKey } from 'utils/uuid';

import {
  APPRAISAL_ORDER_CONTACTS_KEY,
  AppraisalOrderContactKind,
  AppraisalOrderContactSource,
  MANUAL_CONTACT_FIELDS,
  MANUAL_CONTACT_PHONE_FIELDS,
  MANUAL_OPTION_LABEL,
  MANUAL_OPTION_VALUE,
  MAX_OTHER_ORDER_CONTACTS,
  ORDER_CONTACT_FIXED_KINDS,
  ORDER_CONTACT_PINNED_SOURCES,
  OrderContactOptionGroup,
  OrderContactSlot,
  OrderContactSourceLabel,
  UNSAVED_AGENT_HINT,
} from './AppraisalOrderContactsCard.constants';

const { BORROWER, CO_BORROWER, SELLERS_AGENT, BUYERS_AGENT, OTHER } = AppraisalOrderContactKind;
const { LOAN_DEFAULT, UNDERWRITING_AGENT, UNDERWRITING_SELLER, MANUAL, NONE } =
  AppraisalOrderContactSource;

const EMPTY_MANUAL_FIELDS = Object.fromEntries(MANUAL_CONTACT_FIELDS.map(field => [field, null]));

const MANUAL_OPTION = {
  value: MANUAL_OPTION_VALUE,
  label: MANUAL_OPTION_LABEL,
  description: OrderContactSourceLabel.ORDER_ONLY,
  group: OrderContactOptionGroup.OTHER,
  isDisabled: false,
};

const createOrderContactRow = ({ kind, sortOrder, source = LOAN_DEFAULT }) => ({
  id: _.uniqueId('order-contact-'),
  loanAppraisalOrderContactId: null,
  kind,
  source,
  sourceId: null,
  isPrimary: false,
  description: null,
  ...EMPTY_MANUAL_FIELDS,
  sortOrder,
});

const normalizeSavedOrderContact = savedContact => ({
  ...EMPTY_MANUAL_FIELDS,
  ..._.pick(savedContact, MANUAL_CONTACT_FIELDS),
  id: savedContact.loanAppraisalOrderContactId,
  loanAppraisalOrderContactId: savedContact.loanAppraisalOrderContactId,
  kind: savedContact.kind,
  source: savedContact.source ?? LOAN_DEFAULT,
  sourceId: savedContact.sourceId ?? null,
  isPrimary: !!savedContact.isPrimary,
  description: savedContact.description ?? null,
  sortOrder: savedContact.sortOrder ?? 0,
});

const ensurePrimaryOrderContact = rows => {
  const primaryRow = rows.find(({ isPrimary, source }) => isPrimary && source !== NONE);
  const primaryRowId = primaryRow?.id ?? rows.find(({ kind }) => kind === BORROWER)?.id ?? null;

  return rows.map(row => {
    const isPrimary = row.id === primaryRowId;

    return row.isPrimary === isPrimary ? row : { ...row, isPrimary };
  });
};

const updateOrderContactRow = (rows, rowId, getUpdatedRow) =>
  rows.map(row => (row.id === rowId ? getUpdatedRow(row) : row));

export const buildInitialOrderContacts = ({
  savedContacts = [],
  isAgentSlotsVisible,
  isOtherContactsEnabled,
}) => {
  const savedFixedContactsByKind = _.keyBy(
    savedContacts.filter(({ kind }) => kind !== OTHER),
    'kind',
  );

  const fixedRows = ORDER_CONTACT_FIXED_KINDS.filter(
    kind => isAgentSlotsVisible || !OrderContactSlot[kind].isAgentSlot,
  ).map((kind, index) =>
    savedFixedContactsByKind[kind]
      ? normalizeSavedOrderContact(savedFixedContactsByKind[kind])
      : createOrderContactRow({ kind, sortOrder: index }),
  );

  const otherRows = getConditionalArray(isOtherContactsEnabled, () =>
    _.sortBy(
      savedContacts.filter(({ kind }) => kind === OTHER),
      'sortOrder',
    ).map(normalizeSavedOrderContact),
  );

  return ensurePrimaryOrderContact([...fixedRows, ...otherRows]);
};

const getSideDataList = ({ entitySideData, applicationData, key }) =>
  (_.has(entitySideData, key) ? entitySideData[key] : applicationData?.[key]) ?? [];

const getActiveSideDataList = params =>
  getSideDataList(params).filter(({ isDeleted }) => !isDeleted);

export const getPendingDeletedEntityIds = pendingChangesById =>
  new Set(
    Object.keys(pendingChangesById ?? {}).filter(id =>
      _.some(
        pendingChangesById[id],
        change => change?.actionType === HistoryPendingChangesActionType.DELETED,
      ),
    ),
  );

export const excludePendingDeletedOrderContacts = ({ savedContacts = [], pendingChanges }) => {
  const pendingDeletedIds = getPendingDeletedEntityIds(pendingChanges);

  return savedContacts.filter(
    ({ loanAppraisalOrderContactId }) => !pendingDeletedIds.has(loanAppraisalOrderContactId),
  );
};

const getLookupEntities = ({ pendingDeletedEntityIds, ...sideDataParams }) =>
  _.sortBy(
    getActiveSideDataList(sideDataParams).filter(({ id }) => !pendingDeletedEntityIds.has(id)),
    [({ isAdded }) => !!isAdded, ({ id, isAdded }) => (isAdded ? '' : getSqlServerGuidSortKey(id))],
  );

const toBorrowerPerson = (borrower, sourceLabel) => ({
  id: borrower.id,
  fullName: borrower.fullName ?? getPersonFullName(borrower),
  email: borrower.email ?? null,
  phone: normalizeViewPhoneNumber(borrower.cellPhoneNumber) ?? null,
  source: AppraisalOrderContactSource.BORROWER,
  sourceLabel,
  group: OrderContactOptionGroup.BORROWERS,
  isAgent: false,
  isUnsaved: false,
});

const toAgentPerson = ({ agent, sourceLabel, group }) => ({
  id: agent.id,
  fullName: agent.name ?? '',
  email: agent.email ?? null,
  phone: agent.phone ?? null,
  source: UNDERWRITING_AGENT,
  sourceLabel,
  group,
  isAgent: true,
  isUnsaved: !!agent.isAdded,
});

const toSellerPerson = ({ seller, sourceLabel }) => ({
  id: seller.id,
  fullName: seller.sellerName ?? getPersonFullName(seller),
  email: seller.email ?? null,
  phone: seller.phone ?? null,
  source: UNDERWRITING_SELLER,
  sourceLabel,
  group: OrderContactOptionGroup.SELLERS,
  isAgent: true,
  isUnsaved: !!seller.isAdded,
});

export const buildOrderContactsLookup = ({
  borrowers = [],
  entitySideData,
  applicationData,
  pendingDeletedEntityIds = new Set(),
}) => {
  const primaryBorrower =
    borrowers.find(({ borrowerType }) => borrowerType === BorrowerType.PRIMARY) ??
    borrowers[0] ??
    null;
  const isForSaleByOwner =
    (_.has(entitySideData, 'forSaleByOwner')
      ? entitySideData.forSaleByOwner
      : applicationData?.forSaleByOwner) ?? false;
  const sideDataParams = { entitySideData, applicationData, pendingDeletedEntityIds };

  return {
    isForSaleByOwner,
    primaryBorrower: primaryBorrower
      ? toBorrowerPerson(primaryBorrower, OrderContactSourceLabel.PRIMARY_BORROWER)
      : null,
    coBorrowers: borrowers
      .filter(({ id }) => id !== primaryBorrower?.id)
      .map(borrower => toBorrowerPerson(borrower, OrderContactSourceLabel.CO_BORROWER)),
    listingAgents: getLookupEntities({ ...sideDataParams, key: 'listingAgents' }).map(agent =>
      toAgentPerson({
        agent,
        sourceLabel: OrderContactSourceLabel.LISTING_AGENT,
        group: OrderContactOptionGroup.LISTING_AGENTS,
      }),
    ),
    sellingAgents: getLookupEntities({ ...sideDataParams, key: 'sellingAgents' }).map(agent =>
      toAgentPerson({
        agent,
        sourceLabel: OrderContactSourceLabel.SELLING_AGENT,
        group: OrderContactOptionGroup.SELLING_AGENTS,
      }),
    ),
    sellers: getLookupEntities({ ...sideDataParams, key: 'sellers' }).map(seller =>
      toSellerPerson({
        seller,
        sourceLabel: isForSaleByOwner
          ? OrderContactSourceLabel.SELLER_FOR_SALE_BY_OWNER
          : OrderContactSourceLabel.SELLER,
      }),
    ),
  };
};

const getDefaultPerson = (kind, lookup) => {
  switch (kind) {
    case BORROWER:
      return lookup.primaryBorrower;

    case CO_BORROWER:
      return lookup.coBorrowers[0] ?? null;

    case SELLERS_AGENT:
      return (
        lookup.listingAgents[0] ?? (lookup.isForSaleByOwner ? (lookup.sellers[0] ?? null) : null)
      );

    case BUYERS_AGENT:
      return lookup.sellingAgents[0] ?? null;

    default:
      return null;
  }
};

const getAllPersons = lookup => [
  ...getConditionalArray(!!lookup.primaryBorrower, () => [lookup.primaryBorrower]),
  ...lookup.coBorrowers,
  ...lookup.listingAgents,
  ...lookup.sellingAgents,
  ...lookup.sellers,
];

const getCandidatePersons = (kind, lookup) => {
  switch (kind) {
    case BORROWER:
      return getConditionalArray(!!lookup.primaryBorrower, () => [lookup.primaryBorrower]);

    case CO_BORROWER:
      return lookup.coBorrowers;

    case SELLERS_AGENT:
      return [...lookup.listingAgents, ...lookup.sellers];

    case BUYERS_AGENT:
      return lookup.sellingAgents;

    default:
      return getAllPersons(lookup);
  }
};

const getPersonOptionValue = ({ source, id }) => `${source}:${id}`;

const findPinnedPerson = ({ source, sourceId }, lookup) =>
  getAllPersons(lookup).find(
    person => person.source === source && person.id === sourceId && !person.isUnsaved,
  ) ?? null;

const getManualContactPhone = row =>
  MANUAL_CONTACT_PHONE_FIELDS.map(field => row[field]).find(Boolean) ?? null;

const toResolvedPerson = person =>
  person && {
    fullName: person.fullName,
    email: person.email,
    phone: person.phone,
    sourceLabel: person.sourceLabel,
    group: person.group,
    optionValue: getPersonOptionValue(person),
    isAgent: person.isAgent,
    isManual: false,
  };

const resolveManualOrderContact = row => {
  const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ');

  if (!fullName) {
    return null;
  }

  return {
    fullName,
    email: row.email,
    phone: getManualContactPhone(row),
    sourceLabel: OrderContactSourceLabel.MANUAL,
    group: OrderContactOptionGroup.OTHER,
    optionValue: MANUAL_OPTION_VALUE,
    isAgent: false,
    isManual: true,
  };
};

export const resolveOrderContact = ({ row, lookup }) => {
  switch (row.source) {
    case NONE:
      return null;

    case MANUAL:
      return resolveManualOrderContact(row);

    case LOAN_DEFAULT:
      return toResolvedPerson(getDefaultPerson(row.kind, lookup));

    default:
      return toResolvedPerson(findPinnedPerson(row, lookup) ?? getDefaultPerson(row.kind, lookup));
  }
};

const toPersonOption = person => ({
  value: getPersonOptionValue(person),
  label: person.fullName,
  description: person.isUnsaved ? UNSAVED_AGENT_HINT : person.sourceLabel,
  group: person.group,
  isDisabled: person.isUnsaved,
  source: person.source,
  sourceId: person.id,
});

export const getOrderContactOptions = ({ kind, lookup }) => [
  ...getCandidatePersons(kind, lookup)
    .filter(({ fullName }) => !!fullName)
    .map(toPersonOption),
  ...getConditionalArray(!OrderContactSlot[kind].isLocked, [MANUAL_OPTION]),
];

export const getOrderContactSelectValue = ({ contact, isManualEntryOpen }) => {
  if (isManualEntryOpen && !contact?.isManual) {
    return MANUAL_OPTION;
  }

  if (!contact) {
    return null;
  }

  return {
    value: contact.optionValue,
    label: contact.fullName,
    description: contact.sourceLabel,
    group: contact.group,
    isDisabled: false,
  };
};

export const checkIsManualOption = option => option?.value === MANUAL_OPTION_VALUE;

export const selectOrderContactOption = ({ rows, rowId, option, lookup }) =>
  updateOrderContactRow(rows, rowId, row => {
    const currentContact = resolveOrderContact({ row, lookup });

    if (currentContact?.optionValue === option.value) {
      return row;
    }

    const defaultPerson = getDefaultPerson(row.kind, lookup);
    const isDefaultOption = !!defaultPerson && getPersonOptionValue(defaultPerson) === option.value;

    return {
      ...row,
      ...EMPTY_MANUAL_FIELDS,
      source: isDefaultOption ? LOAN_DEFAULT : option.source,
      sourceId: isDefaultOption ? null : option.sourceId,
    };
  });

export const clearOrderContact = ({ rows, rowId }) =>
  ensurePrimaryOrderContact(
    updateOrderContactRow(rows, rowId, row => ({
      ...row,
      ...EMPTY_MANUAL_FIELDS,
      source: NONE,
      sourceId: null,
    })),
  );

export const setPrimaryOrderContact = ({ rows, rowId }) =>
  rows.map(row => ({ ...row, isPrimary: row.id === rowId }));

export const saveManualOrderContact = ({ rows, rowId, values }) =>
  updateOrderContactRow(rows, rowId, row => ({
    ...row,
    ...Object.fromEntries(
      MANUAL_CONTACT_FIELDS.map(field => [field, _.trim(values[field]) || null]),
    ),
    source: MANUAL,
    sourceId: null,
  }));

export const getManualContactFormValues = row =>
  Object.fromEntries(MANUAL_CONTACT_FIELDS.map(field => [field, row?.[field] ?? '']));

const checkIsDefaultOrderContact = ({ kind, source, isPrimary }) =>
  kind === OTHER ? !isPrimary : source === LOAN_DEFAULT && isPrimary === (kind === BORROWER);

export const checkIsOrderContactsDefault = rows => rows.every(checkIsDefaultOrderContact);

export const resetOrderContacts = rows =>
  rows.map(row =>
    row.kind === OTHER
      ? { ...row, isPrimary: false }
      : {
          ...row,
          ...EMPTY_MANUAL_FIELDS,
          source: LOAN_DEFAULT,
          sourceId: null,
          isPrimary: row.kind === BORROWER,
        },
  );

export const checkIsAddOtherOrderContactAllowed = rows =>
  rows.filter(({ kind }) => kind === OTHER).length < MAX_OTHER_ORDER_CONTACTS;

export const addOtherOrderContact = rows => [
  ...rows,
  createOrderContactRow({ kind: OTHER, sortOrder: rows.length, source: NONE }),
];

export const removeOrderContact = ({ rows, rowId }) =>
  ensurePrimaryOrderContact(rows.filter(({ id }) => id !== rowId));

export const setOrderContactDescription = ({ rows, rowId, description }) =>
  updateOrderContactRow(rows, rowId, row => ({ ...row, description }));

const checkIsOrderContactRowChanged = rowPendingChanges =>
  _.some(
    rowPendingChanges,
    change =>
      _.isPlainObject(change) && 'newValue' in change && checkIsPendingChangeHasDifference(change),
  );

export const checkIsOrderContactsChanged = ({ entitySideData, initialEntitySideData }) =>
  _.has(entitySideData, APPRAISAL_ORDER_CONTACTS_KEY) &&
  !_.isEqual(
    entitySideData[APPRAISAL_ORDER_CONTACTS_KEY],
    initialEntitySideData?.[APPRAISAL_ORDER_CONTACTS_KEY],
  );

const getSavedEntityIds = list =>
  new Set(list.filter(({ isAdded }) => !isAdded).map(({ id }) => id));

const sanitizeOrderContactSource = ({ row, savedAgentIds, savedSellerIds }) => {
  const isDanglingAgent = row.source === UNDERWRITING_AGENT && !savedAgentIds.has(row.sourceId);
  const isDanglingSeller = row.source === UNDERWRITING_SELLER && !savedSellerIds.has(row.sourceId);

  if (row.kind === BORROWER || isDanglingAgent || isDanglingSeller) {
    return { ...row, source: LOAN_DEFAULT, sourceId: null };
  }

  return row;
};

const toOrderContactRequestRow = (row, sortOrder) => {
  const isManual = row.source === MANUAL;

  return {
    loanAppraisalOrderContactId: row.loanAppraisalOrderContactId ?? null,
    kind: row.kind,
    source: row.source,
    sourceId: ORDER_CONTACT_PINNED_SOURCES.includes(row.source) ? row.sourceId : null,
    isPrimary: row.isPrimary,
    description: row.kind === OTHER ? _.trim(row.description) || null : null,
    ...Object.fromEntries(
      MANUAL_CONTACT_FIELDS.map(field => [field, isManual ? _.trim(row[field]) || null : null]),
    ),
    sortOrder,
  };
};

export const getAppraisalOrderContactsRequestData = ({ entitySideData, applicationData }) => {
  const sideDataParams = { entitySideData, applicationData };
  const savedAgentIds = getSavedEntityIds([
    ...getActiveSideDataList({ ...sideDataParams, key: 'listingAgents' }),
    ...getActiveSideDataList({ ...sideDataParams, key: 'sellingAgents' }),
  ]);
  const savedSellerIds = getSavedEntityIds(
    getActiveSideDataList({ ...sideDataParams, key: 'sellers' }),
  );

  const sanitizedRows = entitySideData[APPRAISAL_ORDER_CONTACTS_KEY].map(row =>
    sanitizeOrderContactSource({ row, savedAgentIds, savedSellerIds }),
  );

  return ensurePrimaryOrderContact(sanitizedRows).map(toOrderContactRequestRow);
};

const getOrderContactRowKey = ({ row, otherRows }) =>
  row.kind === OTHER ? `other-${otherRows.indexOf(row) + 1}` : _.kebabCase(row.kind);

export const getMissingAgentNote = slot => `No ${slot.missingAgentType} agent on this loan. `;

export const getOrderContactRowsViewData = ({
  rows,
  lookup,
  pendingChanges,
  isHighlightDisabled,
}) => {
  const otherRows = rows.filter(({ kind }) => kind === OTHER);

  return rows.map(row => {
    const slot = OrderContactSlot[row.kind];
    const contact = resolveOrderContact({ row, lookup });
    const options = getOrderContactOptions({ kind: row.kind, lookup });
    const isAgentMissing =
      slot.isAgentSlot &&
      !contact &&
      row.source !== MANUAL &&
      options.every(option => checkIsManualOption(option));

    return {
      row,
      slot,
      contact,
      options,
      isAgentMissing,
      rowKey: getOrderContactRowKey({ row, otherRows }),
      isChanged: !isHighlightDisabled && checkIsOrderContactRowChanged(pendingChanges?.[row.id]),
    };
  });
};
